// Stop React 19's dev-only "Component" performance track from OOMing the tab.
//
// React 19.x's DEVELOPMENT build (react-dom-*.development.js -> `logComponentRender`)
// calls `performance.measure('\u200b' + ComponentName)` for EVERY component on EVERY
// commit (and deep-diffs each component's props per commit). Nothing ever calls
// `performance.clearMeasures()`, so the User Timing buffer grows without bound. The
// Business Coach agents grid commits ~490 components/sec across ~29 live terminals, so
// it reaches millions of PerformanceMeasure objects (~570MB+ renderer heap in ~1hr) and
// crashes the tab with Chrome "Error Code 5" (SBOX_FATAL_MEMORY_EXCEEDED). Confirmed via
// heap snapshots + a live stack trace into logComponentRender.
//
// Production is unaffected (production react-dom has no logComponentRender), so this is
// gated to non-production. The render volume on the agents grid is a separate smell
// worth its own follow-up; this only silences the dev-only instrumentation that leaks.
//
// TWO defenses, in order:
//  1. SOURCE DISABLE (primary, verified): React computes
//        supportsUserTiming = typeof console.timeStamp === 'function'
//                              && typeof performance.measure === 'function'
//     once, when react-dom's module first initializes. Neutralizing `console.timeStamp`
//     BEFORE that init flips it false, so React emits zero measures (and skips the
//     per-commit prop diff). Requires running before react-dom loads -> this module is
//     the FIRST import in index.js.
//  2. BUFFER CAP (durable backstop -- DO NOT DELETE): if a future react-dom changes
//     that gate, or load order ever regresses, defense #1 silently no-ops. The interval
//     below caps the buffer regardless. It self-disables once it confirms the buffer
//     isn't growing (the common case when #1 worked), so it isn't an eternal timer.
//
// Escape hatch: set localStorage 'keepReactPerfTracks' to re-enable React's perf track
// for a session (e.g. to profile component renders) -- at the cost of the leak.

function installReactPerfTrackGuard() {
	if (
		process.env.NODE_ENV === 'production' ||
		typeof console === 'undefined'
	) {
		return;
	}
	try {
		if (
			typeof window !== 'undefined' &&
			window.localStorage &&
			window.localStorage.getItem('keepReactPerfTracks')
		) {
			return; // dev explicitly opted back into React's User Timing track
		}
	} catch {
		// localStorage can throw (privacy mode / sandboxed) -- fall through to guard.
	}

	try {
		// console.timeStamp is a deprecated devtools-timeline marker; nothing in this
		// app calls it (verified). It must become a non-function so React's typeof
		// check fails -- a no-op function would NOT disable the track. The try ensures
		// the buffer cap below still installs even if console is frozen / read-only.
		// eslint-disable-next-line no-console
		console.timeStamp = undefined;
	} catch {
		// console.timeStamp not writable here -- the buffer cap below still bounds it.
	}

	if (
		typeof performance === 'undefined' ||
		typeof performance.clearMeasures !== 'function' ||
		typeof performance.getEntriesByType !== 'function'
	) {
		return;
	}

	// Cap the buffer well below the millions that OOM the tab, with enough headroom
	// for short-lived legitimate profiling. At the observed ~490 measures/sec (only if
	// defense #1 failed), this clears roughly every (20000/490 + 10) ~= 50s.
	const MAX_BUFFERED_MEASURES = 20000;
	const POLL_MS = 10000;
	// Consecutive non-growing polls after which we conclude defense #1 held and stop
	// polling. (The supportsUserTiming gate is one-shot, so once nothing is accruing it
	// never starts mid-session.)
	const STABLE_POLLS_TO_STOP = 3;

	let prevCount = -1;
	let stablePolls = 0;
	const timerId = setInterval(() => {
		let count;
		try {
			count = performance.getEntriesByType('measure').length;
		} catch {
			return; // performance API unavailable mid-session
		}
		if (count > MAX_BUFFERED_MEASURES) {
			performance.clearMeasures();
			prevCount = 0;
			stablePolls = 0;
			return;
		}
		if (count <= prevCount) {
			stablePolls += 1;
			if (stablePolls >= STABLE_POLLS_TO_STOP)
				clearInterval(timerId);
		} else {
			stablePolls = 0;
		}
		prevCount = count;
	}, POLL_MS);

	// Fast Refresh / HMR can re-evaluate this module; cancel the old timer so they
	// don't stack into N concurrent pollers over a long dev session.
	if (typeof module !== 'undefined' && module.hot) {
		module.hot.dispose(() => clearInterval(timerId));
	}
}

installReactPerfTrackGuard();
